import React, { useState } from 'react';

import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  UniqueIdentifier,
  useSensors,
  useSensor,
  MeasuringStrategy,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { observer } from 'mobx-react';

import { Button } from 'components/Button';

import { useStore } from 'utils/hooks/store';

import { DISABLED_FIELD_SUFFIX } from 'stores/EntityFields/EntityFields';
import { FIELD_CONTAINER_ID } from 'stores/EntityLayout/EntityLayout';

import Container from '../Container';

import { dropAnimation } from './types';

import { DraggableItem, Item, SortableItem } from '../Item/Item';

import { EmptyContainer, FieldsScrollArea, FieldsSearchField, LayoutColumns, LayoutScrollArea } from './styles';
import WrapperWithHeader from '../WrapperWithHeader/WrapperWithHeader';
import { DroppableContainer } from '../Container/Container';
import EmptyLayout from '../helpers/EmptyLayout';

const MultipleContainers = (): React.ReactElement => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [distanceFromTop, setDistanceFromTop] = useState(0);

  React.useEffect(() => {
    const calculateDistance = (): void => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDistanceFromTop(rect.top / 2 + 20);
      }
    };

    calculateDistance();
  }, []);

  const { entityFields, entityLayout } = useStore();

  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  const onRemoveSectionFromLayout = (containerId: UniqueIdentifier): void => {
    entityLayout.removeSectionFromLayout(containerId.toLocaleString());
  };

  const { activeId } = entityLayout;

  return (
    <DndContext
      sensors={sensors}
      measuring={{
        droppable: {
          strategy: MeasuringStrategy.Always,
        },
      }}
      onDragStart={({ active }) => entityLayout.onDragStart(active)}
      onDragOver={({ active, over }) => entityLayout.onDragOver(active, over)}
      onDragEnd={({ active, over }) => entityLayout.onDragEnd(active, over)}
      onDragCancel={() => entityLayout.onDragCancel()}
    >
      <LayoutColumns ref={containerRef}>
        <WrapperWithHeader title="Fields" flex={1} distance={distanceFromTop}>
          <FieldsSearchField
            size="small"
            placeholder="Search..."
            value={entityFields.searchQuery}
            onChange={(event) => {
              entityFields.searchFields(event.target.value);
            }}
          />
          <FieldsScrollArea $distance={distanceFromTop}>
            <DroppableContainer
              key={FIELD_CONTAINER_ID}
              id={FIELD_CONTAINER_ID}
              disableActionsHeader
              disabled={false}
              items={entityFields.filteredData?.map(({ id }) => id) ?? []}
              withoutHeader
            >
              {(entityFields.filteredData || []).map(({ name, id }) => (
                <DraggableItem
                  key={id}
                  name={name}
                  id={id as string}
                  disabled={id.toString().includes(DISABLED_FIELD_SUFFIX)}
                />
              ))}
            </DroppableContainer>
          </FieldsScrollArea>
        </WrapperWithHeader>
        <WrapperWithHeader
          flex={2}
          title="Layout"
          helpCenterRedirect
          distance={distanceFromTop}
          unsavedChanges={entityLayout.contentOfLayoutWasEdited}
        >
          <LayoutScrollArea $distance={distanceFromTop}>
            <SortableContext strategy={verticalListSortingStrategy} items={entityLayout.containers}>
              {entityLayout.containers.length === 0 ? (
                <EmptyLayout />
              ) : (
                entityLayout.containers.map((containerId) => {
                  const fixed = entityLayout.isFixedSection(containerId);

                  return (
                    <DroppableContainer
                      id={containerId}
                      key={containerId}
                      items={entityLayout.items[containerId]}
                      label={entityLayout.getSectionTitle(containerId)}
                      fixed={fixed}
                      fixedTooltip={entityLayout.fixedSection?.tooltip}
                      onRemove={fixed ? undefined : () => onRemoveSectionFromLayout(containerId)}
                    >
                      <SortableContext items={entityLayout.items[containerId]}>
                        {entityLayout.items[containerId].length === 0 ? (
                          <EmptyContainer>
                            <InfoOutlinedIcon />
                            Drag & drop a field to start building this section.
                          </EmptyContainer>
                        ) : null}
                        {entityLayout.items[containerId].map((value, index) => (
                          <SortableItem
                            id={value}
                            key={value}
                            index={index}
                            disabled={fixed}
                            name={entityFields.getNameOfField(value as string)}
                            isLast={index === entityLayout.items[containerId].length - 1}
                            required={entityLayout.mustStayInLayout(value as string)}
                            lockMessage={entityLayout.lockedField(value as string)?.message}
                          />
                        ))}
                      </SortableContext>
                    </DroppableContainer>
                  );
                })
              )}

              <Button
                size="large"
                disabled={false}
                variant="outlined"
                style={{ margin: '16px' }}
                onClick={() => entityLayout.addSection()}
              >
                Add section +
              </Button>
            </SortableContext>
            <DragOverlay
              adjustScale={false}
              dropAnimation={dropAnimation}
              // setting maxWidth to 200px to prevent the overlay from taking the whole screen,
              // needed for the drag and drop between containers and fields list
              style={{ maxWidth: activeId && entityLayout.containers.includes(activeId.toString()) ? '' : '200px' }}
            >
              {activeId &&
                (entityLayout.containers.includes(activeId.toString()) ? (
                  <Container
                    label={entityLayout.getSectionTitle(activeId.toString())}
                    isActive
                    id={activeId.toString()}
                  >
                    {entityLayout.items[activeId].map((item) => (
                      <Item
                        key={item}
                        handle={false}
                        value={entityFields.getNameOfField(item as string)}
                        required={entityFields.checkIfFieldIsRequired(item as string)}
                      />
                    ))}
                  </Container>
                ) : (
                  <Item
                    dragOverlay
                    handle={false}
                    value={entityFields.getNameOfField(activeId as string)}
                    required={entityFields.checkIfFieldIsRequired(activeId as string)}
                  />
                ))}
            </DragOverlay>
          </LayoutScrollArea>
        </WrapperWithHeader>
      </LayoutColumns>
    </DndContext>
  );
};

export default observer(MultipleContainers);
